import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { useFetchData } from 'services/api/useFetchData';
import { EMAIL_SETTINGS_PATH, NewsletterSendStatus } from './NewsletterTypes';

// Fetches whether newsletters can be sent from the environment and the remaining quota
export const useNewsletterSendStatus = (): NewsletterSendStatus | null => {
    const { response } = useFetchData({ apiUrl: 'get_newsletter_send_status' });
    return (response as NewsletterSendStatus | null) ?? null;
};

interface NewsletterSendNoticeProps {
    status: NewsletterSendStatus | null;
    compact?: boolean; // Without the link text, as shown inside the send modal
}

// Shows why newsletters cannot be sent yet, or the remaining trial emails
const NewsletterSendNotice: React.FC<NewsletterSendNoticeProps> = ({ status, compact }) => {
    const { t } = useTranslation();
    if (!status) return null;

    // Without an own verified domain nothing can be sent
    if (!status.can_send) {
        return (
            <div className='nl-send-notice nl-send-notice-blocked'>
                <FontAwesomeIcon icon={faTriangleExclamation} />
                <span>
                    {t('newsletters.status.custom_domain_required')}
                    {!compact && <> <Link to={EMAIL_SETTINGS_PATH}>{t('newsletters.status.custom_domain_link')}</Link></>}
                </span>
            </div>
        );
    }

    // In a trial the remaining emails are shown
    if (status.is_trial) {
        return (
            <div className='nl-send-notice nl-send-notice-trial'>
                <FontAwesomeIcon icon={faCircleInfo} />
                <span>{t('newsletters.status.trial_remaining', { remaining: status.trial_emails_remaining ?? 0, limit: status.trial_email_limit })}</span>
            </div>
        );
    }

    return null;
};

export default NewsletterSendNotice;
